import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { UnknownError } from '@atlassian/jira-polaris-lib-errors/src/common/utils/unknown-error/index.tsx';
import { QUICKSTART_THESE_ARE_YOUR_VIEWS_SPOTLIGHT_DISMISSED } from '../../../constants.tsx';
import type { Props, State } from '../../../types.tsx';
import { maybeWaitForPremiumModal } from '../../utils.tsx';

export const loadTheseAreYourViewsSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ getState, setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		await maybeWaitForPremiumModal(getState);

		// User property is already loaded
		if (typeof getState().quickstart.theseAreYourViewsSpotlightDismissed === 'boolean') {
			return;
		}

		try {
			const theseAreYourViewsSpotlightDismissed = await getUserProperty<boolean>(
				accountId,
				QUICKSTART_THESE_ARE_YOUR_VIEWS_SPOTLIGHT_DISMISSED,
			);

			const state = getState();

			setState({
				quickstart: {
					...state.quickstart,
					theseAreYourViewsSpotlightDismissed,
				},
			});
		} catch (error) {
			// Expected error, the property is not set yet
			if (error instanceof FetchError && error.statusCode === 404) {
				setState({
					quickstart: {
						theseAreYourViewsSpotlightDismissed: false,
					},
				});

				return;
			}

			const errorMessage = `Failed to get user property with key: ${QUICKSTART_THESE_ARE_YOUR_VIEWS_SPOTLIGHT_DISMISSED}`;
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.quickstart.these-are-your-views.spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};

export const setTheseAreYourViewsSpotlightDismissed =
	(): Action<State, Props> =>
	async ({ setState }, { accountId }) => {
		if (!accountId) {
			return;
		}

		setState({
			quickstart: {
				theseAreYourViewsSpotlightDismissed: true,
			},
		});

		try {
			await setUserProperties(
				accountId,
				QUICKSTART_THESE_ARE_YOUR_VIEWS_SPOTLIGHT_DISMISSED,
				'true',
			);
		} catch (error) {
			const errorMessage = `Failed to set user property with key: ${QUICKSTART_THESE_ARE_YOUR_VIEWS_SPOTLIGHT_DISMISSED}`;

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.quickstart.these-are-your-views.spotlight',
				errorMessage,
				error instanceof Error ? error : new UnknownError(errorMessage, error),
			);
		}
	};
